import { useState, useEffect } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { useSectionContent } from '@/hooks/useSectionContent';
import { useThemePages } from '@/hooks/useThemePages';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import TemplateRenderer from '@/components/feature/TemplateRenderer';
import HeroBannersTag from '@/components/feature/template-tags/HeroBannersTag';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';
import { getProductImage } from '@/lib/productMedia';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { isUsableThemePage } from '@/lib/themeUsability';
import { getStoreCapabilities } from '@/lib/accountType';
import { isMarketplaceMixedAccount } from '@/lib/marketplace';
import BoutiqueHome from './components/BoutiqueHome';
import ServicesHome from './components/ServicesHome';
import MixteHome from './components/MixteHome';
import RestaurantHome from './components/RestaurantHome';
import MarketplaceHome from './components/MarketplaceHome';
import ZifekBlogSection from '@/components/feature/ZifekBlogSection';
import ThemeExtraBlocksRenderer from '@/components/feature/ThemeExtraBlocksRenderer';
import HomeEventsSection from './components/HomeEventsSection';

interface StatsData {
  productCount: number;
  serviceCount: number;
  partnerCount: number;
  portfolioCount: number;
}

export default function TenantHome() {
  const { tenant, theme } = useTenant();
  const { equivalence } = useDisplayCurrency(tenant?.monaie || 'MAD');
  const produitsHeading = useSectionContent('home-produits');
  const servicesHeading = useSectionContent('home-services');
  const aproposHeading = useSectionContent('home-apropos');
  const questionHeading = useSectionContent('home-question');
  const [stats, setStats] = useState<StatsData>({ productCount: 0, serviceCount: 0, partnerCount: 0, portfolioCount: 0 });
  const [products, setProducts] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { pages: themePages, loading: themePagesLoading } = useThemePages(['home']);

  // Suivi de visite de la page d'accueil (compteur, temps passé, IP)
  useThemePageTracking('home', tenant?.id ?? null, Boolean(tenant));

  useEffect(() => {
    if (!tenant) return;

    const fetchData = async () => {
      try {
        const [prodRes, svcRes, partRes, portRes, featProdRes, featSvcRes] = await Promise.all([
          supabase.from('product_items').select('id', { count: 'exact' }).eq('idcommerce', tenant.id).eq('status', 'active'),
          supabase.from('nospartenairesservices').select('id', { count: 'exact' }).eq('idcommerce', tenant.id),
          supabase.from('partenaires').select('id', { count: 'exact' }).eq('idcommerce', tenant.id),
          supabase.from('portfolio').select('id', { count: 'exact' }).eq('idcommerce', tenant.id),
          supabase.from('product_items').select('*').eq('idcommerce', tenant.id).eq('status', 'active').order('created_at', { ascending: false }).limit(4),
          supabase.from('nospartenairesservices').select('*').eq('idcommerce', tenant.id).order('created_at', { ascending: false }).limit(3),
        ]);

        setStats({
          productCount: prodRes.count || 0,
          serviceCount: svcRes.count || 0,
          partnerCount: partRes.count || 0,
          portfolioCount: portRes.count || 0,
        });
        setProducts(featProdRes.data || []);
        setServices(featSvcRes.data || []);
      } catch {
        // silent
      }
      setLoading(false);
    };

    fetchData();
  }, [tenant]);

  const storeName = resolveStoreName(tenant, theme);

  // ── Design du thème ──────────────────────────────────────────────
  // Si le thème du marchand définit une page d'accueil, on l'affiche telle
  // quelle (mêmes sections, même design que l'aperçu du thème).
  const themeHomeContent = themePages.home?.content;
  const caps = getStoreCapabilities(tenant?.type, tenant?.typecompte);

  // Le thème du marchand garde la priorité s'il définit une vraie page d'accueil.
  if (!themePagesLoading && themeHomeContent && themeHomeContent.trim() && isUsableThemePage(themeHomeContent, theme?.stylesheet)) {
    return (
      <div className="min-h-screen bg-background-50">
        <TemplateRenderer content={themeHomeContent} />
        <ThemeExtraBlocksRenderer />
        <HomeEventsSection />
      </div>
    );
  }

  // Sinon : page d'accueil structurée selon le type de compte.
  // Cas 4 — Restauration : bannière → promo du moment → menu (grille).
  if (!themePagesLoading && tenant && caps.restaurant) {
    return <RestaurantHome />;
  }

  // Cas 5 — Marketplace mixte (produits ET services) : accueil qui agrège
  // les contenus publiés par les vendeurs de la marketplace.
  if (
    !themePagesLoading &&
    tenant &&
    caps.marketplace &&
    caps.products &&
    caps.services &&
    isMarketplaceMixedAccount(tenant.type, tenant.typecompte)
  ) {
    return <MarketplaceHome />;
  }

  // Cas 1 — Boutique (produits uniquement) : bannière → produits en vedette → grille produits.
  if (!themePagesLoading && tenant && caps.products && !caps.services) {
    return <BoutiqueHome />;
  }

  // Cas 2 — Services (services uniquement) : blocs pilotés par la config du marchand.
  if (!themePagesLoading && tenant && caps.services && !caps.products) {
    return <ServicesHome />;
  }

  // Cas 3 — Mixte (produits ET services) : les blocs produits et services
  // sont combinés selon la config du marchand.
  if (!themePagesLoading && tenant && caps.products && caps.services) {
    return <MixteHome />;
  }

  const storeDescription = htmlToText(tenant?.description || tenant?.aboutus);
  const aboutTitle = aproposHeading.title.replace('{storeName}', storeName);
  const aboutText = htmlToText(tenant?.aboutus || tenant?.description);

  // Hardcoded fallback image URL (product.image from DB takes priority)
  const FALLBACK_PRODUCT_IMG = 'https://readdy.ai/api/search-image?query=Professional%20product%20photography%20with%20clean%20minimalist%20studio%20background%20soft%20lighting%20ecommerce%20style%20modern%20aesthetic%20neutral%20tones&width=600&height=600&seq=tenant-home-prod-fallback&orientation=squarish';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Bannières d'accueil (slider) du marchand */}
      <HeroBannersTag />

      {/* Hero Section */}
      <section className="relative py-16 md:py-28 overflow-hidden">
        {tenant.imageaboutus && (
          <>
            <img
              src={tenant.imageaboutus}
              alt={storeName}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-black/45"></div>
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          {tenant?.image && (
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden mx-auto mb-6 border-2 border-background-200/70">
              <img
                src={tenant.image}
                alt={storeName}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}
          <h1 className={`text-3xl md:text-5xl font-bold font-heading mb-3 ${tenant.imageaboutus ? 'text-white drop-shadow' : 'text-foreground-950'}`}>
            {storeName}
          </h1>
          {storeDescription && (
            <p className={`text-sm md:text-base max-w-2xl mx-auto leading-relaxed ${tenant.imageaboutus ? 'text-white/85' : 'text-foreground-500'}`}>
              {storeDescription}
            </p>
          )}

          <div className="flex items-center justify-center gap-6 md:gap-10 mt-8">
            {stats.productCount > 0 && (
              <Link to="/products" className="flex flex-col items-center gap-1 cursor-pointer no-underline group">
                <span className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {stats.productCount}
                </span>
                <span className="text-xs text-foreground-500">Produits</span>
              </Link>
            )}
            {stats.serviceCount > 0 && (
              <Link to="/services" className="flex flex-col items-center gap-1 cursor-pointer no-underline group">
                <span className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {stats.serviceCount}
                </span>
                <span className="text-xs text-foreground-500">Services</span>
              </Link>
            )}
            {stats.partnerCount > 0 && (
              <Link to="/partners" className="flex flex-col items-center gap-1 cursor-pointer no-underline group">
                <span className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {stats.partnerCount}
                </span>
                <span className="text-xs text-foreground-500">Partenaires</span>
              </Link>
            )}
            {stats.portfolioCount > 0 && (
              <Link to="/portfolio" className="flex flex-col items-center gap-1 cursor-pointer no-underline group">
                <span className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {stats.portfolioCount}
                </span>
                <span className="text-xs text-foreground-500">Projets</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="py-10 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                <i className="ri-shopping-bag-line mr-2 text-primary-500"></i>
                {produitsHeading.title}
              </h2>
              <Link to="/products" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap">
                Voir tout <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {products.map((product) => {
                const imgSrc = getProductImage(product.media) || FALLBACK_PRODUCT_IMG;
                const price = Number(product.price || 0);
                const promo = product.discount_enabled && product.discount_price != null;
                return (
                  <div key={product.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 group">
                    <Link to={`/product/${product.id}`} className="block h-48 bg-background-100 overflow-hidden relative no-underline">
                      <img
                        src={imgSrc}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = FALLBACK_PRODUCT_IMG;
                        }}
                      />
                      {promo && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-xs font-semibold">
                          Promo
                        </span>
                      )}
                    </Link>
                    <div className="p-4">
                      <Link to={`/product/${product.id}`} className="no-underline">
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-2 hover:text-primary-600 transition-colors">{product.name}</h3>
                      </Link>
                      <div className="flex items-center gap-2">
                        {promo && product.discount_price != null ? (
                          <>
                            <span className="text-base font-bold text-primary-600">
                              {Number(product.discount_price).toLocaleString()} {tenant?.monaie || 'MAD'}
                            </span>
                            <span className="text-xs text-foreground-400 line-through">
                              {price.toLocaleString()} {tenant?.monaie || 'MAD'}
                            </span>
                          </>
                        ) : (
                          <span className="text-base font-bold text-primary-600">
                            {price ? `${price.toLocaleString()} ${tenant?.monaie || 'MAD'}` : ''}
                          </span>
                        )}
                      </div>
                      {price > 0 && equivalence(promo && product.discount_price != null ? Number(product.discount_price) : price) && (
                        <span className="block mt-0.5 text-[11px] text-foreground-400">
                          {equivalence(promo && product.discount_price != null ? Number(product.discount_price) : price)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Services */}
      {services.length > 0 && (
        <section className="py-10 md:py-16 bg-background-100/50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                <i className="ri-service-line mr-2 text-primary-500"></i>
                {servicesHeading.title}
              </h2>
              <Link to="/services" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap">
                Voir tout <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((service) => (
                <div key={service.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-background-300/60 transition-all duration-200 flex flex-col">
                  <Link to={`/service/${service.id}`} className="no-underline">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 hover:text-primary-600 transition-colors">{service.titre}</h3>
                  </Link>
                  <p className="text-sm text-foreground-500 mb-3 line-clamp-2">{service.description}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-base font-bold text-primary-600">
                      {service.prix ? `${parseFloat(service.prix).toLocaleString()} ${tenant?.monaie || 'MAD'}` : ''}
                    </span>
                    {service.prix && equivalence(parseFloat(service.prix)) && (
                      <span className="text-[11px] text-foreground-400 ml-1">{equivalence(parseFloat(service.prix))}</span>
                    )}
                    {service.tags && (
                      <span className="text-xs px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded-full">
                        {service.tags.split(',')[0].trim()}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/service/${service.id}`}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap"
                  >
                    <i className="ri-eye-line"></i>
                    Voir le service
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About section */}
      {(tenant?.aboutus || tenant?.description) && (
        <section className="py-10 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {aboutTitle}
            </h2>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              {aboutText}
            </p>
            {tenant.adresse && (
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-foreground-500">
                <i className="ri-map-pin-line text-primary-500"></i>
                <span>{[tenant.adresse, tenant.Quartier, tenant.Ville, tenant.Pays].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Blog Zifek — conseils visibles sur le site public du marchand */}
      <ZifekBlogSection />

      {/* CTA */}
      <section className="py-10 md:py-16">
        <div className="w-full px-4 md:px-6 max-w-2xl mx-auto text-center">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8 md:p-10">
            <div className="w-12 h-12 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-send-line text-xl text-accent-600"></i>
            </div>
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">
              {questionHeading.title}
            </h3>
            <p className="text-sm text-foreground-500 mb-5">
              {questionHeading.subtitle}
            </p>
            {tenant?.telephone && (
              <a
                href={`tel:${tenant.telephone}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                {tenant.telephone}
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}